import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNotifications } from '@/contexts/NotificationContext';
import { 
  Menu, X, ChevronDown, LayoutDashboard, TrendingUp, FolderOpen, Archive, 
  Users, FileText, DollarSign, Settings, User, LogOut, Clock, CalendarClock, 
  Home, Briefcase, ChevronLeft, ChevronRight, Bell, Check, Timer, BarChart, 
  CalendarDays, FileDown, CheckCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';


const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const { t, isRTL } = useLanguage();
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearAll } = useNotifications();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });


  const toggleSidebar = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', newState);
  };


  const handleLogout = () => {
    logout();
    navigate('/login');
  };


  // Re-calculate attendance & overtime for badge display
  const attendanceRecords = JSON.parse(localStorage.getItem('attendance_records') || '[]');
  const userAttendance = attendanceRecords.filter(r => r.userId === user.id);
  const attendanceDays = new Set(userAttendance.map(r => new Date(r.clockIn).toDateString())).size;


  const overtimeRequests = JSON.parse(localStorage.getItem('overtime_requests') || '[]');
  const userOvertime = overtimeRequests.filter(r => r.userId === user.id && r.status === 'Approved');
  const overtimeHours = userOvertime.reduce((sum, r) => sum + r.hours, 0);

  // ✅ Pending overtime count for Admin/Manager
  const pendingOvertimeCount = overtimeRequests.filter(r => r.status === 'Pending').length;


  const menuGroups = [
    {
      title: t('platform'),
      items: [
        { to: '/dashboard', icon: LayoutDashboard, label: t('dashboard'), roles: ['Admin', 'Manager', 'Engineer', 'Secretary', 'Employee'] },
        { to: '/pipeline', icon: TrendingUp, label: t('pipeline'), roles: ['Admin', 'Manager', 'Engineer', 'Secretary'] },
        { to: '/projects', icon: FolderOpen, label: t('projects'), roles: ['Admin', 'Manager', 'Engineer', 'Secretary'] },
        { to: '/archived', icon: Archive, label: t('archived'), roles: ['Admin', 'Manager', 'Engineer', 'Secretary'] },
      ]
    },
    {
      title: 'Productivity',
      items: [
        { to: '/productivity/daily', icon: Timer, label: 'Daily Focus', roles: ['Admin', 'Manager', 'Engineer', 'Secretary', 'Employee'] },
        { to: '/productivity/weekly', icon: CalendarDays, label: 'Weekly View', roles: ['Admin', 'Manager', 'Engineer', 'Secretary', 'Employee'] },
        { to: '/productivity/analytics', icon: BarChart, label: 'Analytics', roles: ['Admin', 'Manager', 'Engineer', 'Secretary', 'Employee'] },
        { to: '/productivity/reports', icon: FileDown, label: 'Reports', roles: ['Admin', 'Manager', 'Engineer', 'Secretary', 'Employee'] },
      ]
    },
    {
      title: t('humanResources'),
      items: [
        { to: '/hr/dashboard', icon: Briefcase, label: t('hrDashboard'), roles: ['Admin', 'Manager'] },
        { to: '/hr/residence', icon: Home, label: t('residence'), roles: ['Admin', 'Manager'] },
        { to: '/daily-reports', icon: FileText, label: t('dailyReports'), roles: ['Admin', 'Manager', 'Engineer', 'Secretary', 'Employee'] },
        { to: '/hr/attendance', icon: Clock, label: t('myAttendance'), badge: `${attendanceDays}`, roles: ['Admin', 'Manager', 'Engineer', 'Secretary', 'Employee'] },
        { to: '/hr/overtime', icon: CalendarClock, label: t('myOvertime'), badge: `${overtimeHours}`, roles: ['Admin', 'Manager', 'Engineer', 'Secretary', 'Employee'] },
        // ✅ Admin Overtime Management
        { 
          to: '/admin/overtime', 
          icon: CheckCircle, 
          label: 'Approve Overtime', 
          badge: pendingOvertimeCount > 0 ? `${pendingOvertimeCount}` : null, 
          roles: ['Admin', 'Manager'] 
        },
      ]
    },
    {
      title: t('management'),
      items: [
        { to: '/company-files', icon: FileText, label: t('companyFiles'), roles: ['Admin', 'Manager', 'Engineer', 'Secretary'] },
        { to: '/overhead', icon: DollarSign, label: t('overhead'), roles: ['Admin', 'Manager', 'Engineer', 'Secretary'] },
        { to: '/users', icon: Users, label: t('users'), roles: ['Admin', 'Manager'] },
      ]
    },
    {
      title: t('account'),
      items: [
        { to: '/settings', icon: Settings, label: t('settings'), roles: ['Admin', 'Manager', 'Engineer', 'Secretary', 'Employee'] },
      ]
    }
  ];


  const filteredMenuGroups = menuGroups.map(group => ({
    ...group,
    items: group.items.filter(item => item.roles.includes(user.role))
  })).filter(group => group.items.length > 0);


  const NavItem = ({ item, collapsed }) => (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={item.to}
            onClick={() => setMobileMenuOpen(false)}
            className={cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-white hover:bg-safety-orange transition-all duration-200 group relative",
              collapsed ? "justify-center px-2" : "justify-start"
            )}
          >
            <item.icon className={cn("w-5 h-5 flex-shrink-0 transition-transform group-hover:scale-110", collapsed ? "w-6 h-6" : "")} />
            
            {!collapsed && (
              <span className="font-medium truncate flex-1">{item.label}</span>
            )}
            
            {!collapsed && item.badge && (
               <span className="text-[10px] font-bold bg-charcoal text-white border border-stone-grey px-1.5 py-0.5 rounded-full ml-auto">
                {item.badge}
              </span>
            )}


            {collapsed && item.badge && (
               <span className="absolute top-0 right-0 h-2.5 w-2.5 rounded-full bg-safety-orange border border-navy-steal"></span>
            )}
          </Link>
        </TooltipTrigger>
        {collapsed && (
          <TooltipContent side="right" className="bg-charcoal text-white border-stone-grey ml-2">
            <p>{item.label}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );


  const SidebarContent = ({ collapsed }) => (
    <div className="h-full flex flex-col bg-navy-steal text-white border-r border-charcoal transition-all duration-300">
      <div className={cn("flex items-center border-b border-charcoal h-16", collapsed ? "justify-center p-0" : "justify-between px-6")}>
        {!collapsed && (
          <div>
            <h1 className="text-xl font-bold text-white tracking-tight">Stone Beam</h1>
            <p className="text-[10px] text-stone-grey uppercase tracking-widest">Projects Portal</p>
          </div>
        )}
        {collapsed && (
          <div className="font-bold text-xl text-safety-orange">SB</div>
        )}
      </div>


      <nav className="flex-1 overflow-y-auto overflow-x-hidden p-3 space-y-6 scrollbar-thin scrollbar-thumb-stone-600">
        {filteredMenuGroups.map((group, idx) => (
          <div key={idx}>
            {!collapsed && (
              <h3 className="text-[10px] font-bold text-stone-500 uppercase mb-2 px-3 tracking-wider">{group.title}</h3>
            )}
            {collapsed && idx > 0 && <div className="h-px bg-stone-700/50 my-2 mx-2" />}
            
            <div className="space-y-1">
              {group.items.map((item) => (
                <NavItem key={item.to} item={item} collapsed={collapsed} />
              ))}
            </div>
          </div>
        ))}
      </nav>


      <div className={cn("p-4 border-t border-charcoal bg-navy-steal/50", collapsed ? "items-center justify-center flex" : "")}>
        {!collapsed ? (
           <Button variant="ghost" size="sm" onClick={toggleSidebar} className="w-full text-stone-400 hover:text-white justify-center border border-stone-700/30">
             <ChevronLeft className="w-4 h-4 mr-2" /> Collapse
           </Button>
        ) : (
           <Button variant="ghost" size="icon" onClick={toggleSidebar} className="text-stone-400 hover:text-white h-8 w-8">
             <ChevronRight className="w-4 h-4" />
           </Button>
        )}
      </div>
    </div>
  );


  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop Sidebar (Sticky) */}
      <aside 
        className={cn(
          "hidden lg:block sticky top-0 h-screen z-40 transition-all duration-300 ease-in-out shrink-0",
          isCollapsed ? "w-20" : "w-72"
        )}
      >
        <SidebarContent collapsed={isCollapsed} />
      </aside>


      {/* Mobile Sidebar (Overlay) */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
            />
            <motion.aside
              initial={{ x: isRTL ? 300 : -300 }}
              animate={{ x: 0 }}
              exit={{ x: isRTL ? 300 : -300 }}
              transition={{ type: 'tween', duration: 0.3 }}
              className={`fixed inset-y-0 ${isRTL ? 'right-0' : 'left-0'} w-72 z-50 lg:hidden shadow-2xl`}
            >
              <SidebarContent collapsed={false} />
              <button
                onClick={() => setMobileMenuOpen(false)}
                className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} p-2 text-white hover:text-safety-orange transition-colors`}
              >
                <X className="w-6 h-6" />
              </button>
            </motion.aside>
          </>
        )}
      </AnimatePresence>


      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-navy-steal border-b border-charcoal shadow-md shadow-navy-steal/10 h-16">
          <div className="flex items-center justify-between px-4 h-full">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden p-2 text-white hover:text-safety-orange transition-colors rounded-md hover:bg-white/5"
              >
                <Menu className="w-6 h-6" />
              </button>
              {/* Mobile Logo when sidebar is hidden */}
              <div className="lg:hidden flex flex-col">
                 <span className="text-white font-bold leading-tight">Stone Beam</span>
              </div>
            </div>


            <div className="flex items-center gap-4">
              {/* Notification Bell */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative text-stone-300 hover:text-white hover:bg-white/10 rounded-full w-10 h-10">
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute top-2 right-2 flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-safety-orange opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-safety-orange"></span>
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 p-0 border-stone-grey bg-white text-navy-steal shadow-xl rounded-xl overflow-hidden">
                  <div className="p-4 border-b border-stone-100 flex justify-between items-center bg-slate-50">
                    <h3 className="font-semibold text-sm">Notifications</h3>
                    {unreadCount > 0 && (
                      <Button variant="ghost" size="sm" className="h-6 text-[10px] text-blue-600 hover:text-blue-800 px-2" onClick={markAllAsRead}>
                        Mark all read
                      </Button>
                    )}
                  </div>
                  <ScrollArea className="h-[300px]">
                    {notifications.length > 0 ? (
                      <div className="divide-y divide-stone-100">
                        {notifications.map((notification) => (
                          <div 
                            key={notification.id} 
                            className={cn(
                              "p-4 hover:bg-slate-50 transition-colors flex items-start gap-3 cursor-pointer relative group",
                              !notification.read && "bg-blue-50/50"
                            )}
                            onClick={() => markAsRead(notification.id)}
                          >
                            <div className={cn(
                              "w-2 h-2 rounded-full mt-2 flex-shrink-0",
                              !notification.read ? "bg-safety-orange" : "bg-stone-300"
                            )} />
                            <div className="flex-1">
                              <p className={cn("text-sm font-medium", !notification.read ? "text-navy-steal" : "text-stone-600")}>
                                {notification.title}
                              </p>
                              <p className="text-xs text-stone-500 mt-0.5 line-clamp-2">{notification.description}</p>
                              <p className="text-[10px] text-stone-400 mt-2">{new Date(notification.timestamp).toLocaleString()}</p>
                            </div>
                            {!notification.read && (
                              <Button 
                                variant="ghost" size="icon" 
                                className="h-6 w-6 absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => { e.stopPropagation(); markAsRead(notification.id); }}
                              >
                                <Check className="w-3 h-3 text-blue-600" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-8 text-center text-stone-400 flex flex-col items-center">
                        <Bell className="w-8 h-8 mb-2 opacity-20" />
                        <p className="text-sm">No notifications</p>
                      </div>
                    )}
                  </ScrollArea>
                  <div className="p-2 border-t border-stone-100 bg-slate-50">
                    <Button variant="ghost" size="sm" className="w-full h-8 text-xs text-stone-500 hover:text-red-600" onClick={clearAll}>
                      Clear all notifications
                    </Button>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>


              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-3 hover:bg-charcoal text-white hover:text-white px-3 py-2 h-auto rounded-full lg:rounded-md transition-all">
                   <Avatar className="w-8 h-8 border-2 border-safety-orange">
  <AvatarImage 
    src={user?.avatar} 
    alt={user?.name}
    className="object-cover"
  />
  <AvatarFallback className="bg-safety-orange text-white font-semibold text-xs">
    {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
  </AvatarFallback>
</Avatar>

                    <div className="text-left hidden sm:block">
                      <p className="text-sm font-medium text-white leading-none mb-1">{user.name}</p>
                      <p className="text-xs text-stone-400 leading-none">{user.role}</p>
                    </div>
                    <ChevronDown className="w-4 h-4 text-stone-grey hidden sm:block" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white border-stone-grey text-navy-steal shadow-xl">
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="hover:bg-slate-100 cursor-pointer">
                    <User className="w-4 h-4 mr-2" />
                    {t('profile')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')} className="hover:bg-slate-100 cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />
                    {t('settings')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-stone-grey/20" />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 hover:bg-red-50 cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>


        <main className="flex-1 p-4 lg:p-8 w-full max-w-[1600px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};


export default DashboardLayout;
